import {
  alertAssignmentInternalApi,
  alertFilterApi,
  alertFilterInternalApi,
  alertFilterResultApi,
  ApiAlertType,
  FindAlertFilterResultsOrderByEnum,
} from '../../apis';
import { showFeedback } from '../../feedback/feedbackReducer';
import { Dispatch } from 'redux';
import {
  AssignNextAlertFromFilterOrderByEnum,
  InternalApiAlertExportTemplateEnum,
  InternalApiAlertId,
  InternalApiAlertType,
  InternalApiSortDirection,
} from '../../api-internal';
import { ApiAssignmentUser, ApiFilteredAlert, ApiSortDirection } from '../../api';
import {
  fetchFilteredAlertsBatchSuccess,
  fetchFilteredAlertsSuccess,
  setLoading,
  updateFilteredAlertSuccess,
} from './filteredAlertsReducer';
import { NavigateFunction } from 'react-router';
import { toAlertId } from './AlertFilterResults';
import { Template } from './AlertFilterResultsExportModal';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { parseErrorMessage } from '../../common/errors/helpers';

export const fetchFilteredAlerts =
  (filterId: number, orderBy: FindAlertFilterResultsOrderByEnum, direction: ApiSortDirection, size: number) =>
  async (dispatch: Dispatch) => {
    dispatch(setLoading(true));
    try {
      const result = await alertFilterResultApi.findAlertFilterResults({
        filterId: filterId,
        direction: direction,
        orderBy: orderBy,
        size: size,
      });

      dispatch(fetchFilteredAlertsSuccess({ alerts: result.alerts, filterId }));
    } catch (e) {
      dispatch(fetchFilteredAlertsSuccess({ alerts: [], filterId }));
      dispatch(showFeedback({ message: await parseErrorMessage(e) }));
    }
  };

export const fetchFilteredAlertsNextBatch =
  (
    filterId: number,
    orderBy: FindAlertFilterResultsOrderByEnum,
    direction: ApiSortDirection,
    size: number,
    offsetCreatedTime: Date,
    currentId: number,
    currentType: ApiAlertType
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const result = await alertFilterResultApi.findAlertFilterResults({
        filterId: filterId,
        direction: direction,
        orderBy: orderBy,
        size: size,
        offsetTime: offsetCreatedTime,
        currentId: currentId,
        currentType: currentType,
      });

      dispatch(fetchFilteredAlertsBatchSuccess(result.alerts));
    } catch (e) {
      dispatch(showFeedback({ message: await parseErrorMessage(e) }));
    }
  };

export const assignAndGoToNextAlertInFilter =
  (
    filterId: number,
    direction: InternalApiSortDirection,
    orderBy: AssignNextAlertFromFilterOrderByEnum,
    navigate: NavigateFunction,
    offset?: Date,
    currentAlertId?: number,
    currentAlertType?: InternalApiAlertType
  ) =>
  (dispatch: Dispatch) => {
    alertFilterApi
      .assignNextAlertFromFilter({
        filterId,
        direction,
        orderBy,
        offset,
        currentAlertId,
        currentAlertType,
      })
      .then((result) => {
        const offset = result.assignment?.offset?.toISOString();
        if (result.assignment && result.assignment.alertType === InternalApiAlertType.SCREENING) {
          navigate(
            `/screening/alerts/${result.assignment.alertId}?alertFlow=true&filterId=${filterId}&direction=${direction}&sortBy=${orderBy}&offset=${offset}`
          );
        } else if (result.assignment && result.assignment.alertType === InternalApiAlertType.MONITORING) {
          navigate(
            `/monitoring/alerts/${result.assignment.alertId}?alertFlow=true&filterId=${filterId}&direction=${direction}&sortBy=${orderBy}&offset=${offset}`
          );
        } else if (result.assignment && result.assignment.alertType === InternalApiAlertType.BRIDGE) {
          navigate(
            `/bridge/alerts/${result.assignment.alertId}?alertFlow=true&filterId=${filterId}&direction=${direction}&sortBy=${orderBy}&offset=${offset}`
          );
        } else if (result.assignment && result.assignment.alertType === InternalApiAlertType.MANUAL) {
          navigate(
            `/manual/alerts/${result.assignment.alertId}?alertFlow=true&filterId=${filterId}&direction=${direction}&sortBy=${orderBy}&offset=${offset}`
          );
        } else {
          showConfirmationModal({
            title: '',
            description:
              'No more alerts left in filter to assign to you. Either filter is empty of alerts or alerts have' +
              ' been assigned to other users.',
            confirmationText: 'Ok',
          })(dispatch);
        }
      })
      .catch(async (e) => {
        dispatch(showFeedback({ message: await parseErrorMessage(e) }));
      });
  };

export const assignBulk =
  (filterId: number, assigneeId?: number, alertIds?: InternalApiAlertId[]) => async (dispatch: Dispatch) => {
    try {
      await alertFilterInternalApi.assignAlertsBulk({
        filterId,
        internalApiAlertBulkAssignment: {
          alerts: alertIds,
          assigneeId: assigneeId,
        },
      });
    } catch (e) {
      dispatch(showFeedback({ message: await parseErrorMessage(e) }));
    }
  };

export const exportBulk =
  (filterId: number, template: Template, alertIds?: InternalApiAlertId[], schemaFieldExternalIds?: string[]) =>
  async (dispatch: Dispatch) => {
    try {
      await alertFilterInternalApi.scheduleAlertExport({
        filterId,
        internalApiAlertExport: {
          alerts: alertIds,
          template: InternalApiAlertExportTemplateEnum[template as keyof typeof InternalApiAlertExportTemplateEnum],
          schemaFieldExternalIds,
        },
      });
    } catch (e) {
      dispatch(showFeedback({ message: await parseErrorMessage(e) }));
    }
  };

export const assignFilteredAlert =
  (alert: ApiFilteredAlert, assignee: ApiAssignmentUser) => async (dispatch: Dispatch) => {
    const alertId = toAlertId(alert);
    try {
      await alertAssignmentInternalApi.assignAlert({
        internalApiAlertAssignmentCommand: {
          alertId: alertId.alertId,
          assigneeId: assignee.id,
          alertType: alertId.alertType,
        },
      });

      dispatch(updateFilteredAlertSuccess({ ...alert, assignee }));
    } catch {
      dispatch(
        showFeedback({
          message: 'Failed to assign alert',
          success: false,
        })
      );
    }
  };

export const unassignFilteredAlert = (alert: ApiFilteredAlert) => async (dispatch: Dispatch) => {
  const alertId = toAlertId(alert);
  try {
    await alertAssignmentInternalApi.unassignAlert({
      internalApiAlertAssignmentCommand: {
        alertId: alertId.alertId,
        alertType: alertId.alertType,
      },
    });

    dispatch(updateFilteredAlertSuccess({ ...alert, assignee: undefined }));
  } catch {
    dispatch(
      showFeedback({
        message: 'Failed to unassign alert',
        success: false,
      })
    );
  }
};
